/**
 * Keeps the Zenoti booking links in sync with the selected treatment variant.
 *
 * Each option in the select carries the pre-built Zenoti URLs for that variant
 * (`data-treatment-url` / `data-package-url`), so all URL construction stays in
 * Liquid. An option without a package URL hides the package button entirely.
 */
class TreatmentBooking extends HTMLElement {
  /** @type {HTMLSelectElement | null} */
  get select() {
    return this.querySelector('select[data-treatment-booking-select]');
  }

  /** @type {HTMLAnchorElement | null} */
  get treatmentButton() {
    return this.querySelector('[data-treatment-booking-treatment]');
  }

  /** @type {HTMLAnchorElement | null} */
  get packageButton() {
    return this.querySelector('[data-treatment-booking-package]');
  }

  #controller = new AbortController();

  connectedCallback() {
    const { select } = this;

    if (!select) return;

    select.addEventListener('change', this.#handleChange, { signal: this.#controller.signal });

    // The browser may restore a previous selection on back/forward navigation.
    this.#handleChange();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  #handleChange = () => {
    const option = this.select?.selectedOptions[0];

    if (!option) return;

    this.#applyLink(this.treatmentButton, option.dataset.treatmentUrl);
    this.#applyLink(this.packageButton, option.dataset.packageUrl);
  };

  /**
   * @param {HTMLAnchorElement | null} button
   * @param {string | undefined} url
   */
  #applyLink(button, url) {
    if (!button) return;

    if (url) {
      button.href = url;
      button.hidden = false;
      button.removeAttribute('aria-disabled');
    } else {
      button.removeAttribute('href');
      button.hidden = true;
    }
  }
}

if (!customElements.get('treatment-booking')) {
  customElements.define('treatment-booking', TreatmentBooking);
}
